/**
 * Conectar inversor — passos P3 a P6 do assistente de configuração (out/2026,
 * proposta de correção da configuração inicial do Bruno).
 *
 * - P3: só as marcas testadas (Growatt, Sungrow, Fronius, Ingeteam), cada uma
 *   com o modo de conexão, e "Meu inversor não aparece na lista" (P6, pedido ao
 *   suporte). Antes a lista trazia 10 marcas, quatro delas pedindo App ID ao
 *   suporte do fabricante — o cliente travava.
 * - P4 (login próprio — Growatt, Ingeteam): depois do login, lista as usinas da
 *   conta e liga cada uma ao imóvel (POST /monitoring-system-api/link-plant);
 *   antes o modal fechava e o cliente tinha de achar a planta na edição da UC.
 * - P5 (Sungrow/Fronius — conta mestre): e-mail de compartilhamento DA MARCA
 *   com botão de copiar (Sungrow → operacoes@, Fronius → projetos@) e, se a
 *   conta mestre ainda não vê a usina, o aviso fica na tela em vez de fechar.
 * - Plano Free: a conexão é dos planos pagos — o modal explica que a geração
 *   é informada ao salvar a fatura (o gate de verdade é o backend).
 * Editar credenciais (editItem) segue o formulário de login de antes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'components/lottie/lottie.component';
import { BsCheckLg, BsChevronLeft, BsChevronRight } from 'react-icons/bs';

import { Modal } from 'ui';
import { Button } from 'ui/button/button.ui';

import loadAnimation from 'assets/load/energy.min.json';

import { alert } from 'lib/alert';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as selectMonitoringSystem from 'store/monitoring_system/monitoring_system.selector';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';
import { getConsumerUnitList } from 'store/consumer_unit/consumer_unit.action';
import { currentUser } from 'store/user/user.selector';
import {
  createMonitoringSystemUser,
  updateMonitoringSystemUser,
} from 'store/monitoring_system_user/monitoring_system_user.action';
import { monitoringSystemUserRefresh } from 'store/monitoring_system_user/monitoring_system_user.slice';
import * as selectMonitoringSystemUser from 'store/monitoring_system_user/monitoring_system_user.selector';

import {
  getPlantApiListService,
  linkOwnPlantService,
  linkSharedPlantService,
} from 'services/plant_api.service';
import { getMonitoringSystemLogo } from 'utils/logo';
import { canIntegrateMonitoring } from 'utils/planRules';
import { MonitoringSystem } from 'entities/MonitoringSystem';
import { MonitoringSystemUser } from 'entities/MonitoringSystemUser';
import { PlantApi } from 'entities/PlantApi';

import { CopyableValue } from 'components/setup-wizard/copyable-value.component';
import { UnlistedSetupRequest } from 'components/setup-wizard/unlisted-setup-request.component';
import {
  ChoiceCard,
  ChoiceGrid,
  Field,
  Lead,
  Notice,
  StepTag,
  UnlistedOption,
  WizardBody,
  WizardFooter,
} from 'components/setup-wizard/setup-wizard.styles';

import {
  LoadingPage,
  ModalContent,
} from './modal-associate-monitoring-system.styles';

/** Marcas testadas, na ordem da tela, com o jeito de conectar de cada uma. */
const SUPPORTED_BRANDS: { slug: string; how: string }[] = [
  { slug: 'growatt', how: 'login do app ShinePhone' },
  { slug: 'sungrow', how: 'compartilhar a usina' },
  { slug: 'fronius', how: 'compartilhar a usina' },
  { slug: 'ingeteam', how: 'chave de API do app' },
];

/** Conta mestre por marca (decisão do Bruno: e-mails diferentes, 16/out/2026). */
const SHARED_BRANDS: Record<
  string,
  {
    email: string;
    app: string;
    otherBrand: string;
    inputLabel: string;
    placeholder: string;
    steps: React.ReactNode;
  }
> = {
  sungrow: {
    email: 'operacoes@solsticioenergia.com',
    app: 'iSolarCloud',
    otherBrand: 'A Fronius usa outro, mostrado na tela da Fronius.',
    inputLabel: 'Nome da usina no iSolarCloud',
    placeholder: 'Ex.: Usina Escola Comunitária',
    steps: (
      <>
        No site{' '}
        <a href="https://www.isolarcloud.com" target="_blank" rel="noreferrer">
          isolarcloud.com
        </a>{' '}
        (pelo computador), abra <b>Sistema</b>, clique no ícone de compartilhar
        da sua usina e compartilhe por e-mail com permissão de <b>Espectador</b>
        .
      </>
    ),
  },
  fronius: {
    email: 'projetos@solsticioenergia.com',
    app: 'Solar.web',
    otherBrand: 'A Sungrow usa outro, mostrado na tela da Sungrow.',
    inputLabel: 'Link da usina no Solar.web',
    placeholder: 'https://www.solarweb.com/PvSystems/PvSystem?pvSystemId=...',
    steps: (
      <>
        No{' '}
        <a href="https://www.solarweb.com/" target="_blank" rel="noreferrer">
          Solar.web
        </a>
        , abra a usina e vá em{' '}
        <b>Mais → Configurações → Direitos → Adicionar</b>. O acesso é liberado
        na hora.
      </>
    ),
  },
};

interface ModalAssociationMonitoringSystemProps {
  showModal: boolean;
  hideModal: () => void;
  editItem?: MonitoringSystemUser;
  // Presente apenas quando o modal é aberto DENTRO do cadastro de uma UC
  // (a UC ainda não existe). Nesse contexto Fronius/Sungrow (conta mestre) não
  // podem ser vinculadas a uma UC persistida — o fluxo correto é a etapa
  // "Dados da planta" (ModalPlant). Este callback leva o usuário até lá.
  onSharedPlantDuringCreation?: () => void;
  /** Imóvel já escolhido (ex.: aberto pelo "Primeiros passos" da UC com geração). */
  defaultConsumerUnitId?: string;
  /** Etiqueta do passo quando aberto pelo assistente ("Passo 3 de 3"). */
  stepLabel?: string;
}

type Page = 'select' | 'data' | 'plants' | 'unlisted' | 'loading';

export const ModalAssociateMonitoringSystem = ({
  showModal,
  hideModal,
  editItem,
  onSharedPlantDuringCreation,
  defaultConsumerUnitId,
  stepLabel,
}: ModalAssociationMonitoringSystemProps) => {
  const isEditMode = !!editItem;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector(currentUser);
  const allowed = canIntegrateMonitoring(user?.plan?.name ?? 'Free');

  const monitoringSystemList = useAppSelector(selectMonitoringSystem.list);
  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const monitoringSystemUser = useAppSelector(
    selectMonitoringSystemUser.monitoringSystemUser
  );
  const monitoringSystemUserState = useAppSelector(
    selectMonitoringSystemUser.state
  );
  const monitoringSystemUserAction = useAppSelector(
    selectMonitoringSystemUser.action
  );
  const monitoringSystemUserError = useAppSelector(
    selectMonitoringSystemUser.error
  );

  const [page, setPage] = useState<Page>('select');
  const [selected, setSelected] = useState<MonitoringSystem | null>(null);
  const [identifier, setIdentifier] = useState('');
  const [fields, setFields] = useState<
    { field: string; label: string; value: string }[]
  >([]);

  // Conta mestre (Sungrow/Fronius): nome/link da usina + imóvel
  const [plantRef, setPlantRef] = useState('');
  const [unitId, setUnitId] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingNotice, setPendingNotice] = useState(false);

  // P4: usinas da conta recém-conectada e o imóvel escolhido para cada uma
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [plants, setPlants] = useState<PlantApi[]>([]);
  const [plantUnits, setPlantUnits] = useState<Record<string, string>>({});

  const brands = useMemo(
    () =>
      SUPPORTED_BRANDS.map((b) => ({
        ...b,
        system: monitoringSystemList.find((m) => m.slug === b.slug),
      })).filter((b) => b.system),
    [monitoringSystemList]
  );

  const shared = selected ? SHARED_BRANDS[selected.slug] : undefined;

  useEffect(() => {
    if (!showModal) return;
    setIdentifier(editItem?.identifier ?? '');
    setPlantRef('');
    setUnitId(defaultConsumerUnitId ?? consumerUnitList[0]?.id ?? '');
    setBusy(false);
    setPendingNotice(false);
    setConnectionId(null);
    setPlants([]);
    setPlantUnits({});
    if (isEditMode && editItem) {
      setSelected(editItem.monitoringSystem as MonitoringSystem);
      setFields(
        (editItem.monitoringSystem.signInFields?.fields ?? []).map(
          (x: { field: string; label: string }) => ({ ...x, value: '' })
        )
      );
      setPage('data');
    } else {
      setSelected(null);
      setFields([]);
      setPage('select');
    }
  }, [showModal]);

  // Resultado do create/update da conexão (Redux)
  useEffect(() => {
    if (
      monitoringSystemUserAction !== 'create' &&
      monitoringSystemUserAction !== 'update'
    )
      return;
    if (
      monitoringSystemUserState === 'succeeded' &&
      monitoringSystemUser !== null
    ) {
      dispatch(monitoringSystemUserRefresh());
      if (monitoringSystemUserAction === 'update') {
        alert.success('Credenciais atualizadas com sucesso!');
        hideModal();
        return;
      }
      loadPlants(monitoringSystemUser.id);
    } else if (
      monitoringSystemUserState === 'failed' &&
      monitoringSystemUserError
    ) {
      setPage('data');
      alert.error(monitoringSystemUserError.message);
      dispatch(monitoringSystemUserRefresh());
    }
  }, [monitoringSystemUserState]);

  const loadPlants = async (id: string) => {
    setConnectionId(id);
    setPage('loading');
    try {
      const { data } = await getPlantApiListService({
        monitoringSystemUserId: id,
      });
      const list = data.data ?? [];
      if (list.length === 0) {
        alert.success('Conta conectada! Não encontramos usinas nela ainda.');
        hideModal();
        return;
      }
      setPlants(list);
      // Com um imóvel só, já sugere ele para a 1ª usina
      setPlantUnits(
        consumerUnitList.length === 1
          ? { [list[0].identifier]: consumerUnitList[0].id }
          : {}
      );
      setPage('plants');
    } catch {
      alert.success(
        'Conta conectada! Ligue as usinas aos imóveis na tela do imóvel.'
      );
      hideModal();
    }
  };

  const chooseBrand = (system: MonitoringSystem) => {
    setSelected(system);
    setFields(
      system.signInFields.fields.map((x) => ({
        field: x.field,
        label: x.label,
        value: '',
      }))
    );
  };

  const submitLogin = () => {
    if (!selected) return;
    const signInData = fields.reduce(
      (acc, item) => ({ ...acc, [item.field]: item.value }),
      {} as Record<string, string>
    );
    setPage('loading');
    if (isEditMode && editItem) {
      dispatch(
        updateMonitoringSystemUser({
          monitoringSystemUserId: editItem.id,
          signInData,
          identifier,
        })
      );
    } else {
      dispatch(
        createMonitoringSystemUser({
          monitoringSystemId: selected.id,
          signInData,
          identifier,
        })
      );
    }
  };

  const submitShared = async () => {
    if (!selected || !shared) return;
    if (!unitId) {
      alert.error('Escolha o imóvel da usina.');
      return;
    }
    if (!plantRef.trim()) {
      alert.error(`Informe o ${shared.inputLabel.toLowerCase()}.`);
      return;
    }
    setBusy(true);
    setPendingNotice(false);
    try {
      const response = await linkSharedPlantService({
        plantUrl: plantRef,
        consumerUnitId: unitId,
        systemSlug: selected.slug as 'sungrow' | 'fronius',
      });
      if (response.data.data.status === 'pending') {
        // A conta mestre ainda não vê a usina: o pedido fica registrado e a
        // equipe é avisada, mas o cliente pode corrigir e tentar de novo aqui.
        setPendingNotice(true);
        return;
      }
      alert.success(
        `Usina ${selected.name} conectada! Vamos buscar a geração das faturas que você já enviou.`
      );
      dispatch(getConsumerUnitList());
      hideModal();
    } catch (error: unknown) {
      alert.error(
        (error as { message?: string })?.message ||
          'Não foi possível conectar a usina.'
      );
    } finally {
      setBusy(false);
    }
  };

  const submitPlants = async () => {
    if (!connectionId) return;
    const chosen = plants.filter((p) => plantUnits[p.identifier]);
    if (chosen.length === 0) {
      hideModal();
      return;
    }
    setBusy(true);
    let ok = 0;
    for (const p of chosen) {
      try {
        await linkOwnPlantService({
          monitoringSystemUserId: connectionId,
          identifier: p.identifier,
          consumerUnitId: plantUnits[p.identifier],
        });
        ok += 1;
      } catch (error: unknown) {
        alert.error(
          `${p.name}: ${
            (error as { message?: string })?.message ||
            'não foi possível ligar a usina.'
          }`
        );
      }
    }
    setBusy(false);
    if (ok > 0) {
      alert.success(
        `${ok} usina${ok > 1 ? 's' : ''} conectada${
          ok > 1 ? 's' : ''
        }! Vamos buscar a geração das faturas que você já enviou.`
      );
      dispatch(getConsumerUnitList());
    }
    hideModal();
  };

  const title = isEditMode ? 'Editar credenciais' : 'Conectar inversor';
  const unitOptions = consumerUnitList.map((uc) => (
    <option key={uc.id} value={uc.id}>
      {uc.name} (UC {uc.installation})
    </option>
  ));

  // ── Plano Free: a conexão é dos planos pagos ──────────────────────────────
  if (!allowed && !isEditMode) {
    return (
      <Modal show={showModal} close={hideModal} title={title}>
        <WizardBody>
          <h3>Geração automática nos planos pagos</h3>
          <Lead>
            A conexão com o inversor faz parte dos planos pagos (Starter em
            diante): a geração entra sozinha em cada fatura e você recebe aviso
            se o sistema parar.
          </Lead>
          <Notice tone="info">
            <p>
              No plano Free, consulte no app do seu inversor quanto o sistema
              gerou no período da fatura e informe o número ao salvar a fatura.
            </p>
          </Notice>
        </WizardBody>
        <WizardFooter>
          <Button variant="inverse" type="button" onClick={hideModal}>
            Fechar
          </Button>
          <Button
            type="button"
            onClick={() => {
              hideModal();
              navigate('/conta');
            }}
          >
            Ver planos
          </Button>
        </WizardFooter>
      </Modal>
    );
  }

  return (
    <Modal show={showModal} close={hideModal} title={title}>
      <ModalContent>
        {page === 'loading' || monitoringSystemUserState === 'loading' ? (
          <LoadingPage>
            <Lottie style={{ height: 150 }} animationData={loadAnimation} />
          </LoadingPage>
        ) : page === 'unlisted' ? (
          <UnlistedSetupRequest
            kind="inverter"
            defaultConsumerUnitId={unitId || undefined}
            onBack={() => setPage('select')}
            onClose={hideModal}
          />
        ) : page === 'select' ? (
          <>
            <WizardBody>
              <StepTag>{stepLabel ?? 'Conectar inversor'}</StepTag>
              <h3>Qual é a marca do seu inversor?</h3>
              <Lead>
                Use a conta do app em que você já acompanha a geração.
              </Lead>
              <ChoiceGrid>
                {brands.map(({ slug, how, system }) => (
                  <ChoiceCard
                    key={slug}
                    type="button"
                    selected={selected?.id === system?.id}
                    onClick={() => system && chooseBrand(system)}
                  >
                    <img src={getMonitoringSystemLogo(slug)} alt="" />
                    <span>
                      <strong>{system?.name}</strong>
                      <small>{how}</small>
                    </span>
                  </ChoiceCard>
                ))}
              </ChoiceGrid>
              <UnlistedOption type="button" onClick={() => setPage('unlisted')}>
                <b>Meu inversor não aparece na lista</b>{' '}
                <span>— o suporte configura para você</span>
              </UnlistedOption>
            </WizardBody>
            <WizardFooter>
              <Button variant="inverse" type="button" onClick={hideModal}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={!selected}
                onClick={() => setPage('data')}
              >
                Próximo
                <BsChevronRight />
              </Button>
            </WizardFooter>
          </>
        ) : page === 'plants' ? (
          <>
            <WizardBody>
              <StepTag>{selected?.name}</StepTag>
              <h3>Ligue cada usina ao imóvel</h3>
              <Notice tone="success">
                <p>
                  Login ok. Encontramos{' '}
                  <b>
                    {plants.length} usina{plants.length > 1 ? 's' : ''}
                  </b>{' '}
                  na sua conta. Ligue cada uma ao imóvel que ela abastece.
                </p>
              </Notice>
              {plants.map((p) => (
                <Field key={p.identifier}>
                  {p.name}
                  <select
                    value={plantUnits[p.identifier] ?? ''}
                    onChange={(e) =>
                      setPlantUnits((prev) => ({
                        ...prev,
                        [p.identifier]: e.target.value,
                      }))
                    }
                  >
                    <option value="">Não ligar agora</option>
                    {unitOptions}
                  </select>
                </Field>
              ))}
              <Lead>
                Imóvel ainda não cadastrado? Importe a conta dele e ligue a
                usina depois, na tela do imóvel. Guardamos a senha criptografada
                e só lemos a geração.
              </Lead>
            </WizardBody>
            <WizardFooter>
              <Button variant="inverse" type="button" onClick={hideModal}>
                Fechar
              </Button>
              <Button type="button" onClick={submitPlants} disabled={busy}>
                {busy
                  ? 'Conectando…'
                  : `Conectar ${
                      Object.values(plantUnits).filter(Boolean).length
                    } usina(s)`}
                <BsCheckLg size={20} />
              </Button>
            </WizardFooter>
          </>
        ) : shared ? (
          <>
            <WizardBody>
              <StepTag>{selected?.name}</StepTag>
              {onSharedPlantDuringCreation ? (
                <>
                  <h3>Conectar usina {selected?.name}</h3>
                  <Lead>
                    Você está cadastrando o imóvel agora. A usina é conectada na
                    etapa <b>Dados da planta</b> e salva junto com ele.
                  </Lead>
                  <Button
                    type="button"
                    onClick={() => {
                      onSharedPlantDuringCreation();
                      hideModal();
                    }}
                  >
                    Ir para &quot;Dados da planta&quot;
                    <BsChevronRight />
                  </Button>
                </>
              ) : consumerUnitList.length === 0 ? (
                <Lead>
                  Para conectar a usina, importe primeiro a conta de luz do
                  imóvel e depois volte aqui.
                </Lead>
              ) : (
                <>
                  <h3>Compartilhe sua usina com o EnergyPass</h3>
                  <Lead>
                    A {selected?.name} não libera acesso direto ao dono da
                    usina. O acesso é só de leitura.
                  </Lead>
                  <ol>
                    <li>{shared.steps}</li>
                    <li>
                      Compartilhe com este e-mail:
                      <CopyableValue
                        value={shared.email}
                        copiedMessage="E-mail copiado!"
                      />
                      <small>
                        Este e-mail é só para {selected?.name}.{' '}
                        {shared.otherBrand}
                      </small>
                    </li>
                    <li>
                      Volte aqui e informe a usina, como aparece no {shared.app}
                      .
                    </li>
                  </ol>
                  <Field>
                    {shared.inputLabel}
                    <input
                      value={plantRef}
                      placeholder={shared.placeholder}
                      onChange={(e) => setPlantRef(e.target.value)}
                    />
                  </Field>
                  <Field>
                    Imóvel da usina
                    <select
                      value={unitId}
                      onChange={(e) => setUnitId(e.target.value)}
                    >
                      {unitOptions}
                    </select>
                  </Field>
                  {pendingNotice && (
                    <Notice tone="warn">
                      <p>
                        <b>Ainda não vemos essa usina.</b> Confira se
                        compartilhou com o e-mail acima e se{' '}
                        {selected?.slug === 'sungrow'
                          ? 'o nome está igual ao do app'
                          : 'o link é o da página da usina'}
                        . Avisamos a equipe — se precisar, ela confere o
                        compartilhamento e responde por e-mail.
                      </p>
                    </Notice>
                  )}
                </>
              )}
            </WizardBody>
            <WizardFooter>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setPage('select')}
              >
                <BsChevronLeft />
                Voltar
              </Button>
              {!onSharedPlantDuringCreation && consumerUnitList.length > 0 && (
                <Button type="button" onClick={submitShared} disabled={busy}>
                  {busy ? 'Verificando…' : 'Verificar e conectar'}
                  <BsCheckLg size={20} />
                </Button>
              )}
            </WizardFooter>
          </>
        ) : (
          <>
            <WizardBody>
              <StepTag>{selected?.name}</StepTag>
              {selected?.slug === 'growatt' ? (
                <>
                  <h3>Entre com o login do ShinePhone</h3>
                  <Lead>
                    O mesmo e-mail e senha do app ou do site server.growatt.com.
                  </Lead>
                </>
              ) : selected?.slug === 'ingeteam' ? (
                <>
                  <h3>Cole a chave de API da Ingeteam</h3>
                  <Lead>
                    Entre em{' '}
                    <a
                      href="https://www.ingeconsunmonitor.com/login"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ingeconsunmonitor.com
                    </a>
                    , clique no seu nome de usuário, abra <b>API Access</b> e
                    copie a chave.
                  </Lead>
                </>
              ) : (
                <>
                  <h3>Dados de acesso — {selected?.name}</h3>
                  <Lead>
                    Use o mesmo acesso do app em que você vê a geração.
                  </Lead>
                </>
              )}
              <Field>
                {selected?.identifierField?.label}
                <input
                  name="identifier"
                  value={identifier}
                  autoComplete="username"
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </Field>
              {fields.map((x, index) => (
                <Field key={x.field}>
                  {x.label}
                  <input
                    name={x.field}
                    value={x.value}
                    type={x.field === 'password' ? 'password' : 'text'}
                    autoComplete={
                      x.field === 'password' ? 'current-password' : 'off'
                    }
                    onChange={(e) =>
                      setFields((prev) =>
                        prev.map((f, i) =>
                          i === index ? { ...f, value: e.target.value } : f
                        )
                      )
                    }
                  />
                </Field>
              ))}
              {!isEditMode && (
                <Lead>
                  Guardamos a senha criptografada e só lemos a geração. Depois
                  de conectar, buscamos a geração das faturas que você já
                  enviou.
                </Lead>
              )}
            </WizardBody>
            <WizardFooter>
              <Button
                variant="secondary"
                type="button"
                onClick={() => (isEditMode ? hideModal() : setPage('select'))}
              >
                <BsChevronLeft />
                {isEditMode ? 'Cancelar' : 'Voltar'}
              </Button>
              <Button type="button" onClick={submitLogin}>
                {isEditMode ? 'Salvar' : 'Entrar e listar usinas'}
                <BsCheckLg size={20} />
              </Button>
            </WizardFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
